import { calendarWeekday, melbourneDate } from '../../../shared/dates';
import { briefClock, briefModelLabel } from '../../lib/morningBrief';
import { formatDayMonth } from '../../components/primitives';
import type { AutomationHistoryItem } from '../../components/automationsMobile';
import { STATUS_LABEL, STATUS_TONE } from './constants';
import type { AutomationEmailRow, RunTone } from './types';

/** T-3 / Due / +7d / +14d / +21d — the ladder's read-only stage offsets. */
export function stageLabel(offset: number): string {
  if (offset === 0) return 'Due';
  return offset < 0 ? `T${offset}` : `+${offset}d`;
}

/** "13 Jul, 03:00" — Melbourne calendar date (shared/dates.ts) + local clock
    time, same convention Settings.tsx uses for backup/error timestamps. */
export function runTimeLabel(ms: number): string {
  const d = new Date(ms);
  return `${formatDayMonth(melbourneDate(ms))}, ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function timeOnly(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Send-log rows as the mobile history sheet wants them (mockup §14.1 B):
    subject, "recipient · 10 Sep 03:00", the failure reason, a status pill. */
export function historyItems(rows: AutomationEmailRow[]): AutomationHistoryItem[] {
  return rows.map((row) => ({
    id: row.id,
    subject: row.subject ?? '(no subject)',
    to: row.to_email ?? '—',
    when: `${formatDayMonth(melbourneDate(row.created_at))} ${timeOnly(row.created_at)}`,
    error: row.error,
    statusLabel: STATUS_LABEL[row.status],
    tone: STATUS_TONE[row.status],
    projectId: row.project_id,
  }));
}

/** Classifies the free-text last_result the runners write (via runResult()/
    errorMessage() in automationSettings.ts): "ran, N sent" is ok, "ran,
    nothing due"/"ran, disabled (…)" is idle, "email paused" is paused,
    and anything else (an error message, "email not configured") is err. */
export function runTone(lastResult: string | null): RunTone {
  if (!lastResult) return 'idle';
  if (lastResult === 'email paused') return 'paused';
  if (!lastResult.startsWith('ran,')) return 'err';
  if (/nothing due|disabled/.test(lastResult)) return 'idle';
  return 'ok';
}

const SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The morning brief's heartbeat line (mockup M5). The server writes
    "received 05:01 · claude-opus-5-5 · 14 items", "failed · <error>" or
    "no brief received today"; none start with "ran,", so runTone() would
    call every one an error. */
export function morningBriefStatus(
  lastRunAt: number | null,
  lastResult: string | null,
  enabled = true,
): { tone: RunTone; prefix: string; text: string | null; missed: boolean } {
  if (lastRunAt === null || !lastResult) return { tone: 'idle', prefix: '', text: null, missed: false };
  const status = briefHeartbeat(lastRunAt, lastResult);
  // Switched off: report the last heartbeat quietly, never as an alarm.
  return enabled ? status : { ...status, tone: 'idle', missed: false };
}

function briefHeartbeat(lastRunAt: number, lastResult: string): { tone: RunTone; prefix: string; text: string | null; missed: boolean } {
  const when = `${SHORT_DAYS[calendarWeekday(melbourneDate(lastRunAt))]} ${briefClock(lastRunAt)}`;
  const received = /^received [^·]+· (.+?) · (\d+) items?$/.exec(lastResult);
  if (received) {
    const n = Number(received[2]);
    return { tone: 'ok', prefix: `Last brief ${when}`, text: `${briefModelLabel(received[1])} · ${n} item${n === 1 ? '' : 's'}`, missed: false };
  }
  if (/^no brief received/i.test(lastResult)) return { tone: 'err', prefix: `Checked ${when}`, text: 'No brief received today', missed: true };
  if (lastResult.startsWith('failed')) {
    return { tone: 'err', prefix: `Last brief ${when}`, text: `failed — ${lastResult.replace(/^failed\s*·\s*/, '')}`, missed: false };
  }
  return { tone: 'idle', prefix: `Last run ${when}`, text: lastResult, missed: false };
}
